import { useState } from "react";
import { Alert, Button, Divider, Input, InputNumber, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import ArtifactUploadButton from "./ArtifactUploadButton";
import ActiveVersionSelector from "./ActiveVersionSelector";
import ArtifactDownloadButton from "./ArtifactDownloadButton";
import { KIND_WEASIS, byKind, formatBytes, formatDate, versionsOf } from "./artifact-utils";

/** Weasis (viewer) paketleri: sürüm başına tek weasis.zip. */
export default function ViewerTab({ settings, reload }) {
    const { t } = useTranslation();
    const active = settings.weasis?.versionNumber ?? 0;
    const versions = versionsOf(settings.artifacts, KIND_WEASIS);

    const [uploadVersion, setUploadVersion] = useState(Math.max(active, ...versions, 0) + 1);
    const [label, setLabel] = useState(settings.weasis?.version ?? '');

    const activate = async (versionNumber) => {
        const response = await ApiService.ViewerSettings.SetWeasisVersion({
            versionNumber,
            version: label,
            messages: settings.weasis?.messages ?? []
        });
        if (response?.success) reload();
    };

    const remove = async (versionNumber) => {
        const response = await ApiService.ViewerSettings.DeleteArtifact(KIND_WEASIS, versionNumber);
        if (response?.success) reload();
    };

    const columns = [
        {
            title: t('Version'),
            dataIndex: 'version',
            render: (version) => (
                <Space>
                    <strong>{version}</strong>
                    {version === active && <Tag color="green">{t('Active')}</Tag>}
                </Space>
            )
        },
        { title: t('Size'), dataIndex: 'size', render: formatBytes },
        { title: t('Uploaded At'), dataIndex: 'uploadedAt', render: formatDate },
        { title: t('Uploaded By'), dataIndex: 'uploadedBy', render: (value) => value ?? '—' },
        {
            title: '',
            key: 'actions',
            render: (_, row) => (
                <Space>
                    <ArtifactDownloadButton artifact={row} />
                    <Popconfirm title={t('Delete this package?')} onConfirm={() => remove(row.version)}>
                        <Button size="small" danger disabled={row.version === active}>{t('Delete')}</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message={t('Clients compare their local version number against the active one and download weasis.zip when it is higher.')}
            />

            <ActiveVersionSelector versions={versions} active={active} onActivate={activate} disabled={!label}>
                <div>
                    <Typography.Text type="secondary">{t('Version label shown to clients')}</Typography.Text>
                    <Input
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="4.7.0"
                        style={{ display: 'block', width: 200 }}
                    />
                </div>
            </ActiveVersionSelector>

            <Divider style={{ margin: '8px 0' }} />

            <Space align="end" wrap>
                <div>
                    <Typography.Text type="secondary">{t('New version number')}</Typography.Text>
                    <InputNumber min={1} value={uploadVersion} onChange={setUploadVersion} style={{ display: 'block', width: 160 }} />
                </div>
                <ArtifactUploadButton
                    label={t('Upload weasis.zip')}
                    upload={(file, onProgress) => ApiService.ViewerSettings.UploadWeasis(uploadVersion, file, onProgress)}
                    onUploaded={reload}
                />
            </Space>

            <Table
                rowKey="version"
                size="small"
                pagination={false}
                dataSource={byKind(settings.artifacts, KIND_WEASIS)}
                columns={columns}
            />
        </Space>
    );
}
